import type { ContentDistributionPeriodOut } from '@/api/generated/schemas';

const selected = (
  rows: ContentDistributionPeriodOut[],
  selectedIds: number[]
) => rows.filter((r) => selectedIds.includes(r.id));

/**
 * Rows whose upstream publication has no local row at all — the only state
 * the import endpoint can create from, and what the "not imported" view
 * lists. A row that already has a local publication comes back reported,
 * never rewritten, so it is left out.
 *
 * The ids are content-catalog product ids: what the endpoint takes.
 */
export const importableProductIds = (
  rows: ContentDistributionPeriodOut[],
  selectedIds: number[]
): number[] =>
  Array.from(
    new Set(
      selected(rows, selectedIds)
        .filter((r) => r.user_catalog_publication_id == null)
        .map((r) => r.product_id)
    )
  );

/** Titles for the per-product outcome list; rows without one fall back to `#id`. */
export const importableTitles = (
  rows: ContentDistributionPeriodOut[],
  selectedIds: number[]
): Record<number, string> =>
  Object.fromEntries(
    selected(rows, selectedIds)
      .filter((r) => r.user_catalog_publication_id == null && r.product_title)
      .map((r) => [r.product_id, r.product_title as string])
  );

/**
 * Rows whose upstream publication already lives in the user catalog: what
 * the sync-review wizard walks through, one diff at a time. This includes
 * the in-between row (publication imported, this format missing) — a
 * refresh from upstream is exactly the repair it needs.
 *
 * The ids are user-catalog publication ids: what the diff and apply
 * endpoints take.
 */
export const syncReviewIds = (
  rows: ContentDistributionPeriodOut[],
  selectedIds: number[]
): number[] =>
  Array.from(
    new Set(
      selected(rows, selectedIds)
        .map((r) => r.user_catalog_publication_id)
        .filter((id): id is number => id != null)
    )
  );

/** Titles for the wizard's step header and outcome list; falls back to `#id`. */
export const syncReviewTitles = (
  rows: ContentDistributionPeriodOut[],
  selectedIds: number[]
): Record<number, string> =>
  Object.fromEntries(
    selected(rows, selectedIds)
      .filter(
        (r) =>
          r.user_catalog_publication_id != null &&
          r.user_catalog_publication_title
      )
      .map((r) => [
        r.user_catalog_publication_id as number,
        r.user_catalog_publication_title as string,
      ])
  );
